import { WIDGETS_SEARCH_QUERY_PARAM_NAMES } from "@/components/shared/widgets/constants";
import { WIDGETS } from "@/constants/widgets";
import { GENERAL_FORUM_POST_TYPES } from "shared/lib/constants/forum";
import { EmailInboxTypeI } from "shared/lib/interfaces/email/inbox";
import type { ManageHealthTabI } from "@/modules/manage-blocks/lib/workspaces";
import type { ManagePipelineTabI } from "@/modules/manage-pipeline/lib/tabs";

export const QUERY_PARAMS = {
  LIST: "list",
  NEW_LEAD: "new_lead",
} as const;

const CUSTOMER_DOMAIN = process.env.NEXT_PUBLIC_CUSTOMER_DOMAIN;

const getLandingDomain = () => {
  switch (process.env.NEXT_PUBLIC_ENV) {
    case "local":
      return "http://localhost:3004/";
    case "staging":
      return "https://landing-staging.glencoco.com/";
    default:
      return "https://www.glencoco.com/";
  }
};

const Router = {
  home: () => "/",
  dashboard: () => "/dashboard",
  onboarding: () => "/onboarding",
  verify: () => "/verify",
  coaching: () => "/coaching",
  meetings: () => "/meetings",
  callHistory: () => "/history",
  messages: () => "/messages",
  /**
   * Without arguments this must keep returning exactly "/email-inbox":
   * APP_LAYOUT_SUPPORTED_ROUTES matches on the value.
   */
  emailInbox: (params?: { inboxType?: EmailInboxTypeI; threadId?: string }) => {
    const searchParams = new URLSearchParams();

    if (params?.inboxType) {
      searchParams.set("tab", params.inboxType);
    }

    if (params?.threadId) {
      searchParams.set("thread", params.threadId);
    }

    const query = searchParams.toString();

    return query ? `/email-inbox?${query}` : "/email-inbox";
  },
  performanceDashboard: (tab?: string, metric?: string) => {
    const url = "/performance";

    if (tab || metric) {
      const searchParams = new URLSearchParams({
        tab: tab || "",
        metric: metric || "all",
      });

      return `${url}?${searchParams.toString()}`;
    }

    return url;
  },
  leaderboard: () => "/leaderboard",
  trophies: () => "/trophies",
  // NOTE in stack overflow people ask how to auth0 logout without context
  //      help them and tell about the workaround
  profileInactive: () => "/profile-inactive",
  logout: () => "/logout",
  signup: () => "/signup",
  //Now it is only todos page
  session: () => `/session`,
  settingsProfile: () => `/settings/profile`,
  settingsCallForwarding: () => `/settings/call-forwarding`,
  publicProfile: () => `/settings/profile/public`,
  settingsPayouts: () => `/settings/payouts`,
  settingsAvailability: () => `/settings/availability`,
  settingsReferrals: () => `/settings/referrals`,
  marketplace: () => `/discover`,
  marketplaceCampaign: (campaignId = "", query = "") =>
    encodeURI(`/discover/${campaignId}${query ? `?${query}` : ""}`),
  training: (campaignId = "") => `/campaign/${campaignId}/training`,
  trainingModule: (campaignId = "", trainingModuleId = "") =>
    `/campaign/${campaignId}/training/${trainingModuleId}`,
  myCampaigns: () => `/my-campaigns`,
  /**
   * The caller's dial block week (BACKLOG C-2). `week` is any ET date,
   * YYYY-MM-DD; the page snaps it to that week's Monday. Without arguments
   * this must keep returning exactly "/my-week":
   * APP_LAYOUT_SUPPORTED_ROUTES matches on the value.
   */
  myWeek: (week?: string) =>
    week ? `/my-week?week=${encodeURIComponent(week)}` : "/my-week",
  mySalesFloors: () => `/my-sales-floors`,

  //TODO all account links rename to pipeline
  accounts: (listId?: string) => `/accounts${listId ? `?list=${listId}` : ""}`,
  /** Leads → To-Dos (C-16 S6); exactly "/todos" (APP_LAYOUT_SUPPORTED_ROUTES). */
  todos: () => "/todos",
  lists: (listId?: string) => `/lists${listId ? `?list=${listId}` : ""}`,

  accountDetailsResources: (campaignId: string, accountId: string) =>
    `/accounts/${campaignId}/${accountId}?tab=resources`,

  accountDetailsMessages: (campaignId: string, accountId: string) =>
    `/accounts/${campaignId}/${accountId}?tab=messages`,

  bonuses: () =>
    `https://help.glencoco.com/en/articles/8876957-glencoco-top-callers-reward-program`,

  customerCampaignBillingSettings: (campaignID: string) =>
    `${CUSTOMER_DOMAIN}/campaign/${campaignID}/billing`,

  // The customer app's Account Details widget, where an LGA records a
  // meeting's outcome (C14-W2-D89); widgets open from any page's query string.
  customerAccountDetails: (campaignId: string, accountId: string) =>
    `${CUSTOMER_DOMAIN}/meetings?${new URLSearchParams({
      widget: "account_details",
      campaign_id: campaignId,
      account_id: accountId,
    }).toString()}`,

  createNewLead: () => `${Router.accounts()}?${QUERY_PARAMS.NEW_LEAD}=1`,
  beginAssessment: (campaignId = "") => `/assessment/${campaignId}`,
  salesFloor: () => `/sales-floor`,
  salesFloorGeneralByCategory: (category?: string) =>
    category ? `/sales-floor/general/${category}` : `/sales-floor`,
  salesFloorGeneralByCategoryPost: (category?: string, postId?: string) =>
    category
      ? `/sales-floor/general/${category}/${postId}`
      : `/sales-floor/general/${GENERAL_FORUM_POST_TYPES.DEFAULT}/${postId}`,
  salesFloorByCampaign: ({
    circleSpaceSlug,
  }: {
    circleSpaceSlug?: string | null;
  }) => {
    if (!circleSpaceSlug) {
      return process.env.NEXT_PUBLIC_CIRCLE_COMMUNITY_URL;
    }

    return `${process.env.NEXT_PUBLIC_CIRCLE_COMMUNITY_URL}/s/${circleSpaceSlug}`;
  },
  salesFloorByCampaignPost: ({
    campaignId = "",
    postId = "",
    category,
  }: {
    campaignId: string;
    postId: string;
    category?: string;
  }) =>
    `/sales-floor/campaign/${campaignId}/${
      category ? `${category}/` : ""
    }${postId}`,

  glencocoOpenMarketplace: () => `${getLandingDomain()}/openings`,

  // --- C-14 roles: manage pages (F1 owns this block; F3 appends its pages) ---
  /**
   * The Admin page (roles and pods). Without a tab this must keep returning
   * exactly "/manage/admin": APP_LAYOUT_SUPPORTED_ROUTES matches on the value.
   */
  manageAdmin: (
    tab?: "roles" | "pods" | "rep-workspaces" | "activity" | "settings"
  ) => (tab ? `/manage/admin?tab=${tab}` : "/manage/admin"),
  // The block pages (F3). Without a workspace these must keep returning the
  // bare path: APP_LAYOUT_SUPPORTED_ROUTES matches on the value.
  /**
   * Without arguments this must keep returning exactly "/manage/calendar"
   * (APP_LAYOUT_SUPPORTED_ROUTES). A view (C-12 C's ?view=, e.g. "requests"
   * from a request-waiting alert) opens the Calendar on it, and a campaign
   * (Campaign Health's "See … schedule", C-16 S8) filters it to that campaign.
   */
  manageCalendar: (
    workspaceId?: string,
    view?: string,
    campaignId?: string
  ) => {
    const query = [
      workspaceId && `workspace_id=${encodeURIComponent(workspaceId)}`,
      view && `view=${encodeURIComponent(view)}`,
      campaignId && `campaign_id=${encodeURIComponent(campaignId)}`,
    ].filter(Boolean);

    return query.length
      ? `/manage/calendar?${query.join("&")}`
      : "/manage/calendar";
  },
  /**
   * Team → Health (C-12 wave 2). Without arguments this must keep returning
   * exactly "/manage/health" (APP_LAYOUT_SUPPORTED_ROUTES).
   */
  manageHealth: (
    tab?: ManageHealthTabI,
    workspaceId?: string,
    callerId?: string
  ) => {
    const query = [
      tab && `tab=${tab}`,
      workspaceId && `workspace_id=${encodeURIComponent(workspaceId)}`,
      callerId && `caller=${encodeURIComponent(callerId)}`,
    ].filter(Boolean);

    return query.length
      ? `/manage/health?${query.join("&")}`
      : "/manage/health";
  },
  /** Health's Rep Health tab; a strike alert links to its caller's sheet. */
  manageRepHealth: (workspaceId?: string, callerId?: string) =>
    Router.manageHealth("rep-health", workspaceId, callerId),
  /**
   * Team → Pipeline (C-16 S3). Without arguments this must keep returning
   * exactly "/manage/pipeline" (APP_LAYOUT_SUPPORTED_ROUTES).
   */
  managePipeline: (tab?: ManagePipelineTabI) =>
    tab ? `/manage/pipeline?tab=${tab}` : "/manage/pipeline",
  /** Team → Forecast (C-16 S3); exactly "/manage/forecast" (APP_LAYOUT_SUPPORTED_ROUTES). */
  manageForecast: () => "/manage/forecast",
  // W2 (C-14 phase 2): Team → Analytics.
  manageAnalytics: () => "/manage/analytics",
  // C-14 phase 2 W4: Team → Activity; since C-16 S5 (C16-D450) it only
  // redirects to the Admin page's Activity tab.
  manageActivity: () => "/manage/activity",
  manageLeaderboard: () => "/manage/leaderboard",
  // C-14 phase 2 W3
  manageReps: () => "/manage/reps",
  // --- C-16 S1: Rewards (callers) and Team → Incentives (GLE-8408) ---
  topCalls: () => "/performance/top-calls",
  rewards: () => "/rewards",
  reward: (incentiveId?: string, periodStart?: string) =>
    incentiveId
      ? `/rewards/${encodeURIComponent(incentiveId)}${
          periodStart ? `?period=${encodeURIComponent(periodStart)}` : ""
        }`
      : "/rewards/[incentive_id]",
  manageIncentives: () => "/manage/incentives",
  manageIncentive: (incentiveId?: string) =>
    incentiveId
      ? `/manage/incentives/${encodeURIComponent(incentiveId)}`
      : "/manage/incentives/[incentive_id]",
  manageIncentiveNew: () => "/manage/incentives/new",
  manageIncentiveEdit: (incentiveId?: string) =>
    incentiveId
      ? `/manage/incentives/${encodeURIComponent(incentiveId)}/edit`
      : "/manage/incentives/[incentive_id]/edit",
  /**
   * Team → EOM (C-16 S2). Without arguments this must keep returning exactly
   * "/manage/eom" (APP_LAYOUT_SUPPORTED_ROUTES).
   */
  manageEom: () => "/manage/eom",
  // --- end C-14 block ---

  searchQueryParams: {
    accountDetails: (campaignId: string, accountId: string) =>
      `${WIDGETS_SEARCH_QUERY_PARAM_NAMES.WIDGET_TYPE}=${WIDGETS.MAXED_DIALER}&${WIDGETS_SEARCH_QUERY_PARAM_NAMES.ACCOUNT_ID}=${accountId}&${WIDGETS_SEARCH_QUERY_PARAM_NAMES.CAMPAIGN_ID}=${campaignId}`,
  },
};

export default Router;
